import { useRef } from 'react'
import { useModalDialog } from '../../../hooks'
import { GuestAddForm } from '../components/GuestAddForm'
import { GuestEditForm } from '../components/GuestEditForm'
import { SendInvitesForm } from '../components/SendInvitesForm'
import { GuestBulkEditForm } from '../forms'

// Every dialog's onSubmit must call hideModalDialog() itself — useModalDialog does not
// close on a resolved submit. Repo-wide gotcha, not specific to this app.
//
// There is deliberately NO showEventDialog here: the event create/edit form is a full page
// (views/EventEdit.js at /events/new and /events/:slug/edit), not a modal. The guest-list
// work originally shipped a modal event editor; it was dropped in favour of the page, which
// owns slug generation and the internal-attendee multi-select.
export const useEventDialog = () => {

  const { showModalDialog, hideModalDialog, setLoadingModalDialog } = useModalDialog()
  const guestAddRef = useRef({ submit: () => Promise.resolve() })
  const guestEditRef = useRef({ submit: () => Promise.resolve() })
  const sendInvitesRef = useRef({ submit: () => Promise.resolve() })
  const bulkEditRef = useRef()

  // `data` is { title, slug } — deliberately not the event row itself, so a caller holding
  // only a list row can confirm a deletion.
  const showDeleteEventDialog = (data, onSubmit) => {
    showModalDialog({
      title: 'Ta bort event',
      Content: () => (
        <div>
          Detta tar permanent bort <strong>{data.title || data.slug}</strong>
          {' '}— inklusive hela gästlistan och alla anpassade fält.
        </div>
      ),
      onSubmit: () => onSubmit(data),
      submitLabelLoading: 'Tar bort...',
    })
  }

  // The "Lägg till i kalender" .ics dialog used to live here. It is now the shared
  // hooks/useAddToCalendar, because the landing page offers the same file for a recruitment
  // interview and must not import from this app.

  // Pass the event's slug, its fieldDefs (for the per-guest custom-field inputs),
  // and the person_uuids already on the guest list (so they're excluded from
  // the picker — a person can't be added to the same event twice).
  const showAddGuestsDialog = (eventSlug, fieldDefs, existingPersonUuids, onSaved) => {
    showModalDialog({
      title: 'Lägg till gäster',
      Content: () => (
        <GuestAddForm
          eventSlug={eventSlug}
          fieldDefs={fieldDefs}
          existingPersonUuids={existingPersonUuids}
          dialogRef={guestAddRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onSaved={onSaved}
        />
      ),
      submitLabel: 'Lägg till',
      submitLabelLoading: 'Lägger till...',
      cancelLabel: 'Avbryt',
      onSubmit: () => guestAddRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  // Pass the guest to edit plus that event's fieldDefs (for its custom-field inputs).
  const showEditGuestDialog = (guest, fieldDefs, onSaved) => {
    showModalDialog({
      title: 'Redigera gäst',
      Content: () => (
        <GuestEditForm
          guest={guest}
          fieldDefs={fieldDefs}
          dialogRef={guestEditRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onSaved={onSaved}
        />
      ),
      submitLabel: 'Spara',
      submitLabelLoading: 'Sparar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => guestEditRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  // allCount/selectedCount are plain numbers (computed by the caller); onSubmit
  // receives 'all' or 'selected' and returns the promise that actually sends.
  const showSendInvitesDialog = (allCount, selectedCount, onSubmit) => {
    showModalDialog({
      title: 'Skicka inbjudan',
      Content: () => (
        <SendInvitesForm
          allCount={allCount}
          selectedCount={selectedCount}
          onSubmit={onSubmit}
          dialogRef={sendInvitesRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
        />
      ),
      submitLabel: 'Skicka',
      submitLabelLoading: 'Skickar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => sendInvitesRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  const showDeleteGuestDialog = (guests, onSubmit) => {
    showModalDialog({
      title: 'Ta bort gäst',
      Content: () => (
        <div>
          <div>Är du säker på att du vill ta bort följande?</div>
          <ul>
            <li>{guests.length} gäst{guests.length > 1 ? 'er' : ''} tas bort permanent.</li>
          </ul>
        </div>
      ),
      onSubmit: () => onSubmit(guests),
      submitLabelLoading: 'Tar bort...',
    })
  }

  // Builds the bulk-editable field descriptors (the built-in response column
  // plus this event's custom fields) for the grid editor. `onSubmit` receives
  // a [{ uuid, payload }] list of only the guests whose values changed — see
  // GuestBulkEditForm.
  const showBulkEditGuestDialog = (guests, fieldDefs, onSubmit) => {
    const bulkEditableFields = [
      {
        field: 'event_guest_response', key: 'event_guest_response', label: 'Svar', type: 'select', isCustom: false,
        options: [{ value: '', label: 'Inget svar' }, { value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nej' }],
      },
      ...fieldDefs.map(fd => ({
        field: `event_guest_custom_data.${fd.field_key}`,
        key: fd.field_key,
        label: fd.field_label,
        type: fd.field_type === 'boolean' ? 'checkbox' : fd.field_type === 'number' ? 'number' : 'text',
        isCustom: true,
      })),
    ]

    showModalDialog({
      title: `Redigera fält (${guests.length} gäst${guests.length > 1 ? 'er' : ''})`,
      Content: () => (
        <GuestBulkEditForm
          data={guests}
          fields={bulkEditableFields}
          submitForm={onSubmit}
          ref={bulkEditRef}
        />
      ),
      onSubmit: () => bulkEditRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })),
      submitLabel: 'Spara',
      submitLabelLoading: 'Sparar...',
      cancelLabel: 'Avbryt',
    })
  }

  return {
    showDeleteEventDialog,
    showAddGuestsDialog,
    showEditGuestDialog,
    showSendInvitesDialog,
    showDeleteGuestDialog,
    showBulkEditGuestDialog,
  }
}
